import { UploadTimeline } from "./UploadTimeline";
import { UPLOAD_EVENT, HEALTH } from "./UploadEvents";
import { attachTimelinePersistence } from "./TimelineStorage";
import { getAnalytics } from "../analytics";
import { getClientEnvironment } from "../environment/ClientEnvironment";

/**
 * UploadDiagnostics is the per-upload facade that upload code and bridges
 * write to. It owns:
 *   - the UploadTimeline (ring buffer)
 *   - the current state snapshot (health, retries, chunk counters, etc.)
 *   - fan-out to analytics on every recorded event
 *
 * All modules outside `observability/` interact with diagnostics via this
 * class — they never call analytics.track() or timeline.append() directly.
 * This chokepoint enforces two invariants:
 *   1. Every timeline entry is also an analytics event (no divergence).
 *   2. Every analytics event carries uploadId + baseline context.
 */
export class UploadDiagnostics {
  /**
   * @param {string} uploadId
   * @param {Record<string, any>} [initialContext]  merged into every event
   */
  constructor(uploadId, initialContext = {}) {
    if (!uploadId) {
      throw new Error("UploadDiagnostics requires an uploadId");
    }
    this.uploadId = uploadId;
    this._context = { ...initialContext };
    this._createdAt = Date.now();
    this._state = {
      health: HEALTH.HEALTHY,
      lastProgressAt: this._createdAt,
      lastActivityAt: this._createdAt,
      retryCount: 0,
      chunkCount: null,
      currentChunkIndex: null,
      bytesUploaded: 0,
      totalBytes: null,
      terminalError: null,
      paused: false,
      uploaderState: "created",
      network: null,
    };
    this.timeline = new UploadTimeline(uploadId);
    this._detachTimelinePersistence = attachTimelinePersistence(this.timeline);
    this._stateListeners = new Set();
    this._env = getClientEnvironment();
  }

  // ── context helpers ───────────────────────────────────────────────────────

  /**
   * Merge into the context stamped on every event. Idempotent per-key
   * (subsequent calls overwrite).
   */
  setContext(patch = {}) {
    this._context = { ...this._context, ...patch };
  }

  getContext() {
    return { ...this._context };
  }

  // ── state accessors ───────────────────────────────────────────────────────

  getState() {
    return { ...this._state };
  }

  /**
   * Merge a state patch. Callers pass exactly what changed; the diagnostics
   * object recomputes lastActivityAt automatically.
   *
   * @param {Partial<typeof this._state>} patch
   */
  patchState(patch) {
    if (!patch) return;
    this._state = { ...this._state, ...patch };
    this._state.lastActivityAt = Date.now();
    this._notifyState();
  }

  /** Subscribe to state changes (admin UI live view). */
  subscribeState(listener) {
    if (typeof listener !== "function") return () => {};
    this._stateListeners.add(listener);
    return () => this._stateListeners.delete(listener);
  }

  // ── recording ─────────────────────────────────────────────────────────────

  /**
   * Record a diagnostic event. This is the ONLY method the rest of the
   * codebase should call for observability writes.
   *
   * @param {string} event                one of UPLOAD_EVENT.*
   * @param {Record<string, any>} [metadata]
   * @param {{ skipAnalytics?: boolean }} [options]
   */
  record(event, metadata = {}, options = {}) {
    const payload = { ...metadata };
    const entry = this.timeline.append(event, payload);
    this._applyImplicitStatePatch(event, payload);

    if (!options.skipAnalytics) {
      const analytics = getAnalytics();
      analytics.captureUploadEvent(this.uploadId, event, {
        ...this._context,
        ...payload,
        $env_browser: this._env.browser,
        $env_os: this._env.os,
      });
    }
    return entry;
  }

  /**
   * Convenience for "one line, no metadata" logs.
   */
  info(event, metadata) {
    return this.record(event, metadata);
  }

  // ── lifecycle ─────────────────────────────────────────────────────────────

  /**
   * Called by the registry when the upload reaches a terminal state and the
   * registry decides to evict this diagnostics object. Detaches persistence
   * so the last state is flushed once more before we stop mirroring.
   */
  dispose({ removeStoredTimeline: shouldRemove = false } = {}) {
    if (this._detachTimelinePersistence) {
      this._detachTimelinePersistence();
      this._detachTimelinePersistence = null;
    }
    if (shouldRemove) {
      // Lazy import to avoid a cycle in tests that mock TimelineStorage.
      import("./TimelineStorage").then(({ removeStoredTimeline }) => {
        removeStoredTimeline(this.uploadId).catch(() => {});
      });
    }
    this._stateListeners.clear();
  }

  // ── internals ─────────────────────────────────────────────────────────────

  /**
   * Some events carry state implications the caller shouldn't have to
   * duplicate (e.g. chunk_completed means "make progress; reset stuck
   * timer"). Centralising these keeps bridges terse and prevents drift.
   */
  _applyImplicitStatePatch(event, metadata) {
    switch (event) {
      case UPLOAD_EVENT.UPLOAD_STARTED:
        // lastProgressAt reset: an upload that sat queued for longer than the
        // stuck threshold must not be flagged POSSIBLY_STUCK the instant it
        // finally starts moving.
        this.patchState({
          uploaderState: "uploading",
          lastProgressAt: Date.now(),
        });
        break;
      case UPLOAD_EVENT.CHUNK_COMPLETED:
        this.patchState({
          lastProgressAt: Date.now(),
          bytesUploaded:
            typeof metadata.bytesUploaded === "number"
              ? metadata.bytesUploaded
              : this._state.bytesUploaded,
          currentChunkIndex:
            typeof metadata.chunkIndex === "number"
              ? metadata.chunkIndex
              : this._state.currentChunkIndex,
        });
        break;
      case UPLOAD_EVENT.CHUNK_FAILED:
      case UPLOAD_EVENT.RETRY_STARTED:
        this.patchState({ retryCount: this._state.retryCount + 1 });
        break;
      case UPLOAD_EVENT.UPLOAD_PAUSED:
        this.patchState({ paused: true, uploaderState: "paused" });
        break;
      case UPLOAD_EVENT.UPLOAD_RESUMED:
        // lastProgressAt reset: only CHUNK_COMPLETED advances the progress
        // clock, so without this a pause longer than the stuck threshold made
        // the very next sweep flag the freshly-resumed upload as stuck and
        // emit a phantom "stuck" snapshot.
        this.patchState({
          paused: false,
          uploaderState: "uploading",
          lastProgressAt: Date.now(),
        });
        break;
      case UPLOAD_EVENT.UPLOAD_COMPLETED:
        this.patchState({
          uploaderState: "completed",
          health: HEALTH.COMPLETED,
        });
        break;
      case UPLOAD_EVENT.UPLOAD_CANCELLED:
        // Terminal: without this case a cancelled upload kept its previous
        // state forever, so the stuck sweeper flagged it POSSIBLY_STUCK 60s
        // after every user cancel and its "stuck" snapshot overwrote the real
        // backend snapshot (latest-wins) — phantom evidence of stuck uploads.
        this.patchState({
          uploaderState: "cancelled",
          health: HEALTH.CANCELLED,
        });
        break;
      case UPLOAD_EVENT.UPLOAD_FAILED:
        this.patchState({
          uploaderState: "failed",
          health: HEALTH.FAILED,
          terminalError:
            metadata?.error || metadata?.message || "unknown_error",
        });
        break;
      case UPLOAD_EVENT.NETWORK_OFFLINE:
        this.patchState({ network: { ...(metadata || {}), online: false } });
        break;
      case UPLOAD_EVENT.NETWORK_ONLINE:
      case UPLOAD_EVENT.CONNECTION_CHANGED:
        this.patchState({ network: { ...(metadata || {}), online: true } });
        break;
      default:
        break;
    }
  }

  _notifyState() {
    for (const listener of this._stateListeners) {
      try {
        listener(this._state);
      } catch (_error) {
        /* observers must not break writers */
      }
    }
  }
}
